import { auth, callFirebaseFunction, db, storage } from 'boot/firebase';
import { collection, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { getDownloadURL, getMetadata, ref, updateMetadata } from 'firebase/storage';
import { get, groupBy, isEmpty, pick } from 'lodash';
import GenericIndexedDb from 'src/indexed-db/generic-indexedDb';
import { auth as useAuthStore } from 'src/store/auth';
import { firebaseUploader as useFirebaseUploaderStore } from 'src/store/firebaseUploader';
import AppUtils from 'src/utils/AppUtils';
import { BACKUP_DATA, BACKUP_NOTES } from 'src/utils/backupUtils';
import { findImagesToUploadReferences } from 'src/utils/OfflineAccessUtils';

const SESSION_ACTIVITIES_COLLECTION_NAME = 'sessionActivities';

async function updateSessionActivityDoc(updateData, sessionActivityId) {
  const sessionActivityCollectionRef = collection(db, SESSION_ACTIVITIES_COLLECTION_NAME);
  const sessionActivityRef = doc(sessionActivityCollectionRef, sessionActivityId);

  const dataToUpdate = {
    ...updateData,
    updateTimestamp: serverTimestamp(),
    updateBy: get(auth, ['currentUser', 'uid']) || null,
  };

  return updateDoc(sessionActivityRef, dataToUpdate);
}

/**
 * @param {string} newNotes
 * @param {string} sessionActivityId
 * @param {string} session
 */
function updateActivityNotesLocalBackup(newNotes, sessionActivityId, session) {
  const authStore = useAuthStore();
  const clientId = authStore?.currentClient?.id;

  if (clientId) {
    const aggregatedBackupData = {
      id: `backupNotes_${sessionActivityId}`,
      session,
      client: clientId,
      notes: newNotes,
      fieldsData: null,
      isBackupAddedOnline: true,
    };

    const sessionBackupNotesDb = new GenericIndexedDb(BACKUP_NOTES);
    return sessionBackupNotesDb.set(aggregatedBackupData, aggregatedBackupData.id);
  }
}

/**
 * @param {string} initNotes
 * @param {Object[]} filesData
 * @return {string}
 */
function embedFilesUrlsIntoActivityNotes(initNotes, filesData) {
  const sessionActivityNotesImagesData = filesData.filter((data) => !!(data.imageId));

  const imagesUploadingReferences = findImagesToUploadReferences(initNotes);
  const groupedReferences = groupBy(imagesUploadingReferences, 'referenceId');
  let newNotes = initNotes;

  sessionActivityNotesImagesData.forEach((data) => {
    const [reference] = groupedReferences[data.imageId] || [];
    if (!reference) {
      return;
    }

    newNotes = newNotes.replaceAll(reference.attrs, `src="${data.url}"`);
  });

  return newNotes;
}

/**
 * @param {Object[]} signatures
 * @param {Object[]} filesData
 * @return {Object[]} updated signatures
 */
function embedFilesUrlsIntoActivitySignatures(signatures, filesData) {
  const sessionActivitySignatureFileData = filesData.filter((data) => !!(data.signatureId));
  const groupedSignatures = groupBy(signatures, 'id');

  return sessionActivitySignatureFileData.map((data) => {
    const [fullSignature] = groupedSignatures[data.signatureId] || [];
    if (!fullSignature) {
      return;
    }

    return {
      ...fullSignature,
      url: data.url,
    };
  }).filter((data) => !!(data));
}

/**
 * @param {{ signatureId?: string, imageId?: string, sessionActivityId: string, url: string}} updateActivityData
 */
async function updateSessionActivityWithUploadedFiles(updateActivityData) {
  const sessionActivitiesIdxDb = new GenericIndexedDb(SESSION_ACTIVITIES_COLLECTION_NAME);
  const groupedSessionActivityUpdateData = groupBy(updateActivityData, 'sessionActivityId');

  const updatingSignaturesActions = Object.keys(groupedSessionActivityUpdateData).map(async (sessionActivityId) => {
    const localSessionActivity = await sessionActivitiesIdxDb.read(sessionActivityId);
    const { signatures, notes } = localSessionActivity;

    const sessionActivityFilesData = groupedSessionActivityUpdateData[sessionActivityId]; // array of objects

    const updatedSignatures = embedFilesUrlsIntoActivitySignatures(signatures, sessionActivityFilesData);
    const updatedNotes = embedFilesUrlsIntoActivityNotes(notes, sessionActivityFilesData);

    await updateActivityNotesLocalBackup(updatedNotes, sessionActivityId, localSessionActivity.session);

    return updateSessionActivityDoc({ notes: updatedNotes, signatures: updatedSignatures }, sessionActivityId);
  });

  return AppUtils.allSettled(updatingSignaturesActions);
}

/**
 * @param {Object[]} filesData
 * @return {Promise<void>}
 */
async function uploadFiles(filesData) {
  const actions = filesData.map(async (fileInfo) => {
    const { destinationPath, metadata } = fileInfo;
    const dataToReturn = {};

    const callback = async (storageFileRef) => {
      const downloadUrl = await getDownloadURL(storageFileRef);

      if (metadata?.customMetadata) {
        // For now it is only possible to upload session activity notes images and signatures
        if (metadata?.customMetadata.signature) {
          // eslint-disable-next-line prefer-destructuring
          dataToReturn.sessionActivityId = destinationPath.split('/')[4];
          dataToReturn.signatureId = fileInfo.id;
        } else {
          // eslint-disable-next-line prefer-destructuring
          dataToReturn.sessionActivityId = destinationPath.split('/')[1];
          dataToReturn.imageId = fileInfo.id;
        }
      }

      dataToReturn.url = downloadUrl;
    };

    const firebaseUploader = useFirebaseUploaderStore();
    await firebaseUploader.addToUploadQueue({
      ...fileInfo,
      callback,
    });

    return dataToReturn;
  });

  const uploadResults = await AppUtils.allSettled(actions, (err) => throw new Error(err));
  const updateActivityData = uploadResults.map((result) => result.value).filter((data) => !!(data));

  return updateSessionActivityWithUploadedFiles(updateActivityData);
}

async function updateFileMetadata(filePath, metadata) {
  const fileRef = ref(storage, filePath);
  const currentMetadata = await getMetadata(fileRef);
  const newMetadata = {
    customMetadata: {
      ...currentMetadata.customMetadata,
      ...metadata,
    },
  };

  return updateMetadata(fileRef, newMetadata);
}

/**
 * @param {Array<Object>} filesUpdatingData
 * @return {Promise<void>}
 */
async function updateFilesMetadata(filesUpdatingData) {
  const actions = filesUpdatingData.map(async (updateData) => {
    const { filePath, metadata } = updateData;
    return updateFileMetadata(filePath, metadata);
  });

  return AppUtils.allSettled(actions);
}

// eslint-disable-next-line import/prefer-default-export
export function useOfflineMode() {
  async function syncToOnline() {
    const DB_CHANGES_TABLE = 'dbChanges';

    const idxDb = new GenericIndexedDb(DB_CHANGES_TABLE);
    const dbChanges = await idxDb.readAll([], 'timestamp', 'asc');

    const dataChanges = [];
    if (dbChanges.length) {
      // eslint-disable-next-line no-restricted-syntax
      for (const change of dbChanges) {
        const { id, collectionName, operation, documentId: docId, timestamp } = change;

        idxDb.setCollectionPath(collectionName);
        // eslint-disable-next-line no-await-in-loop
        const originalDoc = await idxDb.read(docId);
        let payload = null;

        if (operation === 'create') {
          payload = originalDoc || {};
        } else if (operation === 'update') {
          // get values transformed from idxDB to Firestore format (important for Timestamps)
          const transformedPayload = pick(originalDoc, Object.keys(change.payload));
          // @todo: refactor
          // eslint-disable-next-line no-nested-ternary
          payload = originalDoc
            ? transformedPayload
            : isEmpty(transformedPayload)
              ? change.payload || {}
              : transformedPayload;
        }

        dataChanges.push({
          id,
          operation,
          collectionName,
          documentId: originalDoc?.id || null,
          idxDb: docId,
          payload,
          timestamp,
        });
      }
    }

    // Syncing files
    const FILE_UPLOADING_TABLE = 'filesUploading';
    const filesUploadingDb = new GenericIndexedDb(FILE_UPLOADING_TABLE);
    const filesData = await filesUploadingDb.readAll();

    const FILE_UPDATING_TABLE = 'filesUpdating';
    const filesUpdatingDb = new GenericIndexedDb(FILE_UPDATING_TABLE);
    const filesUpdatingData = await filesUpdatingDb.readAll();

    const clientsMappingForSync = JSON.parse(localStorage.getItem('clientsMappingForSync'));

    const filteredDataChanges = dataChanges.filter((dataChange) => {
      return ![BACKUP_DATA, BACKUP_NOTES].includes(dataChange.collectionName);
    });

    try {
      if (dataChanges.length) {
        await callFirebaseFunction('syncToDatabase', { dataChanges: filteredDataChanges });
        idxDb.setCollectionPath(DB_CHANGES_TABLE);
        await idxDb.clearTable();
      }

      if (filesData.length) {
        await uploadFiles(filesData);
        await filesUploadingDb.clearTable();
      }

      if (filesUpdatingData.length) {
        await updateFilesMetadata(filesUpdatingData);
        await filesUpdatingDb.clearTable();
      }

      if (clientsMappingForSync && clientsMappingForSync.length) {
        await callFirebaseFunction('mapSessionWithAppointments', { clientsIds: clientsMappingForSync });
        localStorage.removeItem('clientsMappingForSync');
      }
    } catch (err) {
      console.error('Syncing error', err);
      throw new Error(err);
    }
  }

  return {
    syncToOnline,
  };
}
